import React from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { ThemeProvider, CssBaseline, Box } from "@mui/material";

import PrivateRoute from "./components/PrivateRoute";
import AdminRoute from "./components/AdminRoute";
import ErrorBoundaryWrapper from "./components/ErrorBoundary";
import OnboardingGuard from "./components/OnboardingGuard";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import theme from "./theme/theme";
import { usePresenceHeartbeat } from "./hooks/usePresenceHeartbeat";

import LandingPage from "./pages/LandingPage.jsx";
import PresentationPage from "./pages/PresentationPage.jsx";
import PricingPage from "./pages/PricingPage";
import CheckoutPage from "./pages/CheckoutPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import Dashboard from "./pages/Dashboard";
import AdminEntryPage from "./pages/AdminEntryPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import OnboardingPage from "./pages/OnboardingPage";
import PreInterviewForm from "./pages/PreInterviewForm";
import VoiceInterviewPage from "./pages/VoiceInterviewPage";
import ReportPage from "./pages/ReportPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import InterviewsPage from "./pages/InterviewsPage";
import CommunicationPracticePage from "./pages/CommunicationPracticePage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminOverviewPage from "./pages/admin/AdminOverviewPage";
import AdminSecurityPage from "./pages/admin/AdminSecurityPage";
import AdminCandidatesPage from "./pages/admin/AdminCandidatesPage";
import AdminCandidateDetailPage from "./pages/admin/AdminCandidateDetailPage";
import AdminInterviewsPage from "./pages/admin/AdminInterviewsPage";
import AdminTrialsPage from "./pages/admin/AdminTrialsPage";
import AdminExportsPage from "./pages/admin/AdminExportsPage";
import AdminConfigPage from "./pages/admin/AdminConfigPage";
import AdminQuestionBankPage from "./pages/admin/AdminQuestionBankPage";
import NotFoundPage from "./pages/NotFoundPage";
import AdminMfaEnrollPage from "./pages/auth/AdminMfaEnrollPage";
import { RuntimeModeProvider } from "./contexts/RuntimeModeContext";
import DemoModeBanner from "./components/DemoModeBanner";
import ApplicationShell from "./components/ApplicationShell";
import RoadmapPage from "./pages/RoadmapPage";

function AppShell({ navbar = false, footer = false }) {
  return (
    <Box
      sx={{
        minHeight: "100dvh",
        backgroundColor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {navbar && <Navbar />}
      <Box component="main" sx={{ flex: 1 }}>
        <Outlet />
      </Box>
      {footer && <Footer />}
    </Box>
  );
}

const MainLayout = () => <ApplicationShell />;
const PublicLayout = () => <AppShell footer />;
const LandingLayout = () => <AppShell />;

function AuthLayout() {
  return (
    <Box
      sx={{
        minHeight: "100dvh",
        backgroundColor: "background.default",
      }}
    >
      <Outlet />
    </Box>
  );
}

export default function App() {
  usePresenceHeartbeat();
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <RuntimeModeProvider>
        <DemoModeBanner variant="inline" />

        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
        <Routes>
          {/* Landing route without footer */}
          <Route element={<LandingLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/presentation" element={<PresentationPage />} />
          </Route>

          {/* Public routes without navbar */}
          <Route element={<PublicLayout />}>
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/checkout/:planKey" element={<CheckoutPage />} />
          </Route>

          <Route element={<AuthLayout />}>
            <Route path="/login/*" element={<LoginPage />} />
            <Route path="/register/*" element={<RegisterPage />} />
            <Route path="/forgot-password/*" element={<ForgotPasswordPage />} />
            <Route path="/set-password/*" element={<SetPasswordPage />} />
            <Route path="/admin" element={<AdminEntryPage />} />
            <Route path="/admin/login/*" element={<AdminLoginPage />} />
            <Route path="/admin/mfa-enroll" element={<AdminMfaEnrollPage />} />
          </Route>

          {/* Auth-protected onboarding route (unguarded) */}
          <Route
            element={
              <PrivateRoute>
                <MainLayout />
              </PrivateRoute>
            }
          >
            <Route path="/onboarding" element={<OnboardingPage />} />
          </Route>

          <Route
            path="/admin/dashboard"
            element={
              <PrivateRoute signInUrl="/admin/login">
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              </PrivateRoute>
            }
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<AdminOverviewPage />} />
            <Route path="security" element={<AdminSecurityPage />} />
            <Route path="candidates" element={<AdminCandidatesPage />} />
            <Route path="candidates/:candidateUserId" element={<AdminCandidateDetailPage />} />
            <Route path="interviews" element={<AdminInterviewsPage />} />
            <Route path="question-bank" element={<AdminQuestionBankPage />} />
            <Route path="trials" element={<AdminTrialsPage />} />
            <Route path="exports" element={<AdminExportsPage />} />
            <Route path="config" element={<AdminConfigPage />} />
          </Route>

          {/* Auth-protected routes with navbar + onboarding completed */}
          <Route
            element={
              <PrivateRoute>
                <OnboardingGuard>
                  <MainLayout />
                </OnboardingGuard>
              </PrivateRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/history" element={<Navigate to="/dashboard" replace state={{ legacyRedirect: "history" }} />} />
            <Route path="/interviews" element={<InterviewsPage />} />
            <Route path="/tracks" element={<InterviewsPage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />
            <Route path="/communication-practice" element={<CommunicationPracticePage />} />
            <Route path="/interview-config" element={<PreInterviewForm />} />
            <Route path="/report" element={<Navigate to="/dashboard" replace state={{ legacyRedirect: "report" }} />} />
            <Route path="/report/:sessionId" element={<ReportPage />} />
          </Route>

          {/* Interview session routes - full screen, no navbar */}
          <Route
            path="/interview"
            element={
              <PrivateRoute>
                <Navigate to="/interviews" replace />
              </PrivateRoute>
            }
          />

          <Route
            path="/interview/:type"
            element={
              <PrivateRoute>
                <OnboardingGuard>
                  <ErrorBoundaryWrapper>
                    <VoiceInterviewPage />
                  </ErrorBoundaryWrapper>
                </OnboardingGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/interview/session/:sessionId"
            element={
              <PrivateRoute>
                <OnboardingGuard>
                  <ErrorBoundaryWrapper>
                    <VoiceInterviewPage />
                  </ErrorBoundaryWrapper>
                </OnboardingGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="*"
            element={<NotFoundPage />}
          />
        </Routes>
        </BrowserRouter>
      </RuntimeModeProvider>
    </ThemeProvider>
  );
}
